"use client"

// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useInView, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import contactImage from '../../assets/contact.jpg';
import API_ENDPOINTS from '../../config/api';
import {
  GraduationCap,
  Building2,
  Users,
  Star,
  ArrowRight,
  Search,
  Filter,
  Clock,
  CheckCircle,
  Award,
  Globe,
  MapPin,
  TrendingUp,
  BookOpen,
  Shield,
  Zap,
  Target,
  Loader2,
  Heart,
  Lightbulb,
  Network,
  Rocket,
  Play,
  Calendar,
  CalendarIcon,
  UserCheck,
  ArrowUpRight,
  Sparkles,
  Brain,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  DollarSign,
  Eye,
  X,
  MessageCircle,
  AlertCircle,
} from 'lucide-react';
import QuickSearchBar from '../../components/common/QuickSearchBar';
import TrainingCard from '../../components/common/TrainingCard';
import { trainingsService, Training } from '../../services/trainings.service';
import { trainingCatalogService, TrainingResponse } from '../../services/training-catalog.service';
import sessionsService, { Session } from '../../services/sessions.service';
import { contactService, ContactFormData } from '../../services/contact.service';
import { slidersService, SliderItem, SliderColorVariant } from '../../services/sliders.service';
import CountdownTimer from '../../components/common/CountdownTimer';
import { formatTrainingLocation } from '../../utils/address';
import heroBg from '../../assets/hero.jpg';
import collaborerBg from '../../assets/collaborer.jpg';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

// Animation variants for modern landing page effects
const fadeUpVariants: any = {
  hidden: {
    opacity: 0,
    y: 60,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1
  }
};

const staggerContainer: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1
  }
};

const slideInVariants: any = {
  hidden: {
    opacity: 0,
    x: -50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1
  }
};

const scaleVariants: any = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0
  }
};

const zoomOutVariants: any = {
  hidden: {
    opacity: 0,
    scale: 1.3
  },
  visible: {
    opacity: 1,
    scale: 1
  }
};

const moveUpVariants: any = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0
  }
};

const pulseVariants: any = {
  hover: {
    scale: 1.05
  },
  tap: {
    scale: 0.95
  }
};

const floatingVariants: any = {
  animate: {
    y: [-10, 10, -10]
  }
};

// Typing animation component
const TypingAnimation: React.FC<{ text: string; speed?: number; delay?: number }> = ({
  text,
  speed = 50,
  delay = 0
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (currentIndex < text.length) {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }
    }, speed);

    return () => clearTimeout(timeout);
  }, [currentIndex, text, speed]);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      setCurrentIndex(0);
      setDisplayedText('');
    }, delay);

    return () => clearTimeout(initialTimeout);
  }, [delay]);

  return (
    <span>
      {displayedText}
    </span>
  );
};

// Scroll reveal component
const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: {
          opacity: 1,
          y: 0
        }
      }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Objectif Section Component
const ObjectifSection: React.FC = () => {
  const { t } = useTranslation();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Enhanced sequential animation variants for one-by-one entrance
  const cardVariants: any = {
    hidden: {
      opacity: 0,
      y: 60,
      scale: 0.9,
      rotateX: 10
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.8,
        delay: i * 0.2, // Increased delay for more noticeable one-by-one effect
        ease: [0.22, 1, 0.36, 1], // Custom cubic-bezier for smooth deceleration
        opacity: {
          duration: 0.6,
          delay: i * 0.2
        },
        scale: {
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: i * 0.2
        }
      }
    }),
  };

  const detailVariants: any = {
    initial: {
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    },
  };

  const cardDetails = {
    trouver: {
      title: t('home.objectif.trouver.title'),
      description: t('home.objectif.trouver.description'),
      features: t('home.objectif.trouver.features', { returnObjects: true }) as string[],
      stats: t('home.objectif.trouver.stats', { returnObjects: true }) as { centres: string; consultants: string; services: string },
    },
    apprendre: {
      title: t('home.objectif.apprendre.title'),
      description: t('home.objectif.apprendre.description'),
      features: t('home.objectif.apprendre.features', { returnObjects: true }) as string[],
      stats: t('home.objectif.apprendre.stats', { returnObjects: true }) as { formations: string; apprenants: string; certificats: string },
    },
    exceller: {
      title: t('home.objectif.exceller.title'),
      description: t('home.objectif.exceller.description'),
      features: t('home.objectif.exceller.features', { returnObjects: true }) as string[],
      stats: t('home.objectif.exceller.stats', { returnObjects: true }) as { experts: string; performances: string; conformité: string },
    },
    collaborer: {
      title: t('home.objectif.collaborer.title'),
      description: t('home.objectif.collaborer.description'),
      features: t('home.objectif.collaborer.features', { returnObjects: true }) as string[],
      stats: t('home.objectif.collaborer.stats', { returnObjects: true }) as { partenaires: string; programmes: string; utilisateurs: string },
    },
    croitre: {
      title: t('home.objectif.croitre.title'),
      description: t('home.objectif.croitre.description'),
      features: t('home.objectif.croitre.features', { returnObjects: true }) as string[],
      stats: t('home.objectif.croitre.stats', { returnObjects: true }) as { pays: string; collaborations: string; impact: string },
    },
  };

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-brand-surface via-purple-50 to-pink-50 px-4 py-12 md:py-20">
      <div className="mx-auto max-w-7xl w-4/5">
        {/* Header */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid gap-8 md:grid-cols-2 md:items-center"
          >
            {/* Left Side - Main Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="mb-6 text-5xl font-bold leading-tight text-gray-900 md:text-6xl lg:text-7xl">
                {t('home.objectif.title')}{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-black">
                    PHINLEX
                  </span>
                  <span className="absolute inset-0 bg-brand-accent -skew-y-1 transform"></span>
                </span>
              </h2>
            </motion.div>

            {/* Right Side - Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center justify-center md:justify-end"
            >
              <p
                className="max-w-md text-lg text-gray-600 leading-relaxed"
                style={{
                  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '0.01em'
                }}
              >
                {t('home.objectif.subtitle')}
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Hero Content */}
        <div className="mb-8 text-center md:mb-12">
          {/* Modern Bento Grid */}
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {/* Card 1: Trouver - Clean White Card */}
            <div className="relative">
              <AnimatePresence mode="wait">
                {expandedCard !== "trouver" ? (
                  <motion.div
                    key="trouver-preview"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15, margin: "0px 0px -100px 0px" }}
                    variants={cardVariants as any}
                    custom={0}
                    whileHover={{
                      y: -8,
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }}
                    className="group relative overflow-hidden rounded-3xl bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-2xl border border-gray-100"
                  >
                    <div className="mb-6">
                      <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 group-hover:bg-brand-primary/10 transition-colors duration-300">
                        <Search className="h-8 w-8 text-gray-900 group-hover:text-brand-primary transition-colors duration-300" />
                      </div>
                      <h3 className="mb-3 text-2xl font-bold text-gray-900">{cardDetails.trouver.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {cardDetails.trouver.description}
                      </p>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-2">
                      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-900">
                        {t('home.objectif.trouver.badges.certifiedCenters')}
                      </span>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-900">
                        {t('home.objectif.trouver.badges.expertConsultants')}
                      </span>
                    </div>
                    <button
                      onClick={() => setExpandedCard("trouver")}
                      className="mt-6 flex items-center gap-2 text-sm font-semibold text-gray-900 transition-all hover:gap-3 group-hover:text-brand-primary"
                    >
                      {t('home.objectif.trouver.learnMore')}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="trouver-detail"
                    variants={detailVariants as any}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-2xl border border-gray-100"
                  >
                    <button
                      onClick={() => setExpandedCard(null)}
                      className="absolute right-4 top-4 z-10 rounded-full bg-gray-100 p-2 transition-colors hover:bg-gray-200"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="mb-4 flex items-center gap-3">
                      <Search className="h-8 w-8 text-brand-primary" />
                      <h3 className="text-2xl font-bold text-gray-900">{cardDetails.trouver.title}</h3>
                    </div>
                    <p className="mb-6 text-sm text-gray-700 leading-relaxed">{cardDetails.trouver.description}</p>
                    <div className="mb-6 space-y-2">
                      {cardDetails.trouver.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-brand-primary" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 border-t border-gray-200 pt-4">
                      <div>
                        <div className="text-lg font-bold text-gray-900">{cardDetails.trouver.stats.centres}</div>
                        <div className="text-xs text-gray-600">{t('home.objectif.trouver.stats.centresLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{cardDetails.trouver.stats.consultants}</div>
                        <div className="text-xs text-gray-600">{t('home.objectif.trouver.stats.consultantsLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{cardDetails.trouver.stats.services}</div>
                        <div className="text-xs text-gray-600">{t('home.objectif.trouver.stats.servicesLabel')}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Card 2: Apprendre - Gradient Card */}
            <div className="relative">
              <AnimatePresence mode="wait">
                {expandedCard !== "apprendre" ? (
                  <motion.div
                    key="apprendre-preview"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15, margin: "0px 0px -100px 0px" }}
                    variants={cardVariants as any}
                    custom={1}
                    whileHover={{
                      y: -8,
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }}
                    onClick={() => setExpandedCard("apprendre")}
                    className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-secondary to-pink-600 p-8 text-white shadow-lg transition-all duration-300 hover:shadow-2xl cursor-pointer"
                  >
                    <div className="mb-6">
                      <div className="mb-4 flex -space-x-2">
                        <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40" />
                        <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40" />
                        <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40" />
                      </div>
                      <h3 className="mb-3 text-2xl font-bold">{cardDetails.apprendre.title}</h3>
                      <p className="text-sm text-white/90 leading-relaxed">
                        {cardDetails.apprendre.description}
                      </p>
                    </div>
                    <Link
                      to="/trainings"
                      className="mt-6 w-full rounded-full bg-white px-6 py-3 text-center font-semibold text-brand-secondary transition-colors hover:bg-white/90"
                    >
                      {t('home.objectif.apprendre.exploreTrainings')}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.div
                    key="apprendre-detail"
                    variants={detailVariants as any}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-secondary to-pink-600 p-8 text-white shadow-2xl"
                  >
                    <button
                      onClick={() => setExpandedCard(null)}
                      className="absolute right-4 top-4 rounded-full bg-white/20 p-2 transition-colors hover:bg-white/30"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="mb-4 flex items-center gap-3">
                      <GraduationCap className="h-8 w-8" />
                      <h3 className="text-2xl font-bold">{cardDetails.apprendre.title}</h3>
                    </div>
                    <p className="mb-6 text-sm text-white/90 leading-relaxed">{cardDetails.apprendre.description}</p>
                    <div className="mb-6 space-y-2">
                      {cardDetails.apprendre.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 border-t border-white/20 pt-4">
                      <div>
                        <div className="text-lg font-bold">{cardDetails.apprendre.stats.formations}</div>
                        <div className="text-xs text-white/70">{t('home.objectif.apprendre.stats.formationsLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold">{cardDetails.apprendre.stats.apprenants}</div>
                        <div className="text-xs text-white/70">{t('home.objectif.apprendre.stats.apprenantsLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold">{cardDetails.apprendre.stats.certificats}</div>
                        <div className="text-xs text-white/70">{t('home.objectif.apprendre.stats.certificatsLabel')}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Card 3: Exceller - Dark Card */}
            <div className="relative md:col-span-2 lg:col-span-1">
              <AnimatePresence mode="wait">
                {expandedCard !== "exceller" ? (
                  <motion.div
                    key="exceller-preview"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15, margin: "0px 0px -100px 0px" }}
                    variants={cardVariants as any}
                    custom={2}
                    whileHover={{
                      y: -8,
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }}
                    onClick={() => setExpandedCard("exceller")}
                    className="group relative overflow-hidden rounded-3xl bg-gray-900 p-8 text-white shadow-lg transition-all duration-300 hover:shadow-2xl cursor-pointer"
                  >
                    <div className="mb-6">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 group-hover:bg-brand-mint/20 transition-colors duration-300">
                        <Rocket className="h-6 w-6 text-white group-hover:text-brand-mint transition-colors duration-300" />
                      </div>
                      <h3 className="mb-3 text-2xl font-bold">{cardDetails.exceller.title}</h3>
                      <p className="text-sm text-white/80 leading-relaxed">
                        {cardDetails.exceller.description}
                      </p>
                    </div>
                    <div className="mt-6">
                      <div className="text-3xl font-bold text-brand-mint">95%</div>
                      <div className="text-sm text-white/60">{t('home.objectif.exceller.satisfactionRate')}</div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="exceller-detail"
                    variants={detailVariants as any}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="relative overflow-hidden rounded-3xl bg-gray-900 p-8 text-white shadow-2xl"
                  >
                    <button
                      onClick={() => setExpandedCard(null)}
                      className="absolute right-4 top-4 rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="mb-4 flex items-center gap-3">
                      <Rocket className="h-8 w-8 text-brand-mint" />
                      <h3 className="text-2xl font-bold">{cardDetails.exceller.title}</h3>
                    </div>
                    <p className="mb-6 text-sm text-white/80 leading-relaxed">{cardDetails.exceller.description}</p>
                    <div className="mb-6 space-y-2">
                      {cardDetails.exceller.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-brand-mint" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 border-t border-white/10 pt-4">
                      <div>
                        <div className="text-lg font-bold">{cardDetails.exceller.stats.experts}</div>
                        <div className="text-xs text-white/60">{t('home.objectif.exceller.stats.expertsLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold">{cardDetails.exceller.stats.performances}</div>
                        <div className="text-xs text-white/60">{t('home.objectif.exceller.stats.performancesLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold">{cardDetails.exceller.stats.conformité}</div>
                        <div className="text-xs text-white/60">{t('home.objectif.exceller.stats.conformitéLabel')}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Card 4: Collaborer - Large Card with Image */}
            <div className="relative md:col-span-2">
              <AnimatePresence mode="wait">
                {expandedCard !== "collaborer" ? (
                  <motion.div
                    key="collaborer-preview"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15, margin: "0px 0px -100px 0px" }}
                    variants={cardVariants as any}
                    custom={3}
                    whileHover={{
                      y: -8,
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 25px 25px -12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                    }}
                    onClick={() => setExpandedCard("collaborer")}
                    className="group relative overflow-hidden rounded-3xl bg-gray-900 transition-all duration-300 cursor-pointer"
                    style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
                    data-collaborer-card
                  >
                    {/* Background Image */}
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${collaborerBg})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-400/30 to-gray-500/40" />

                    <div className="relative z-10 p-8 text-left">
                      <div className="mb-6">
                        {/* Logo */}
                        <div className="mb-4">
                          <img
                            src="/logophone.png"
                            alt="Phinlex Logo"
                            className="h-8 w-auto"
                          />
                        </div>
                        <h3 className="mb-3 text-2xl font-bold text-white">{cardDetails.collaborer.title}</h3>
                        <p className="mb-6 text-sm text-white/80 leading-relaxed">
                          {cardDetails.collaborer.description}
                        </p>
                      </div>

                      {/* Rating Section */}
                      <div className="flex items-center gap-4 rounded-2xl bg-black/40 px-6 py-4 backdrop-blur-sm w-fit">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-brand-accent text-brand-accent" />
                          ))}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">4.9/5</div>
                          <div className="text-xs text-white/60">{t('home.objectif.collaborer.lovedBy')}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="collaborer-detail"
                    variants={detailVariants as any}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="relative overflow-hidden rounded-3xl bg-gray-900 shadow-2xl"
                  >
                    {/* Background Image for Expanded View */}
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                      style={{ backgroundImage: `url(${collaborerBg})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-400/40 to-gray-500/50" />

                    <div className="relative z-10 p-8">
                      <button
                        onClick={() => setExpandedCard(null)}
                        className="absolute right-4 top-4 z-20 rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                      <div className="mb-4 flex items-center gap-3">
                        <Users className="h-8 w-8 text-brand-accent" />
                        <h3 className="text-2xl font-bold text-white">{cardDetails.collaborer.title}</h3>
                      </div>
                      <p className="mb-6 text-sm text-white/80 leading-relaxed">{cardDetails.collaborer.description}</p>
                      <div className="mb-6 space-y-2">
                        {cardDetails.collaborer.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
                            <span className="text-sm text-white">{feature}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-4 border-t border-white/10 pt-4">
                        <div>
                          <div className="text-lg font-bold text-white">{cardDetails.collaborer.stats.partenaires}</div>
                          <div className="text-xs text-white/60">{t('home.objectif.collaborer.stats.partenairesLabel')}</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-white">{cardDetails.collaborer.stats.programmes}</div>
                          <div className="text-xs text-white/60">{t('home.objectif.collaborer.stats.programmesLabel')}</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-white">{cardDetails.collaborer.stats.utilisateurs}</div>
                          <div className="text-xs text-white/60">{t('home.objectif.collaborer.stats.utilisateursLabel')}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Card 5: Croître - Accent Color Card */}
            <div className="relative">
              <AnimatePresence mode="wait">
                {expandedCard !== "croitre" ? (
                  <motion.div
                    key="croitre-preview"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15, margin: "0px 0px -100px 0px" }}
                    variants={cardVariants as any}
                    custom={4}
                    whileHover={{
                      y: -8,
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }}
                    onClick={() => setExpandedCard("croitre")}
                    className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFDE59] to-yellow-400 p-8 shadow-lg transition-all duration-300 hover:shadow-2xl cursor-pointer"
                  >
                    <div className="mb-6">
                      <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-black/10 group-hover:bg-black/20 transition-colors duration-300">
                        <Globe className="h-8 w-8 text-gray-900 group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <h3 className="mb-3 text-2xl font-bold text-gray-900">{cardDetails.croitre.title}</h3>
                      <p className="text-sm text-gray-900/80 leading-relaxed">
                        {cardDetails.croitre.description}
                      </p>
                    </div>
                    <div className="mt-6 flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-gray-900" />
                        <span className="text-sm font-semibold text-gray-900">{cardDetails.croitre.stats.pays} {t('home.objectif.croitre.countries')}</span>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="croitre-detail"
                    variants={detailVariants as any}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFDE59] to-yellow-400 p-8 shadow-2xl"
                  >
                    <button
                      onClick={() => setExpandedCard(null)}
                      className="absolute right-4 top-4 rounded-full bg-black/10 p-2 transition-colors hover:bg-black/20"
                    >
                      <X className="h-4 w-4 text-gray-900" />
                    </button>
                    <div className="mb-4 flex items-center gap-3">
                      <Globe className="h-8 w-8 text-gray-900" />
                      <h3 className="text-2xl font-bold text-gray-900">{cardDetails.croitre.title}</h3>
                    </div>
                    <p className="mb-6 text-sm text-gray-900/80 leading-relaxed">{cardDetails.croitre.description}</p>
                    <div className="mb-6 space-y-2">
                      {cardDetails.croitre.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-gray-900" />
                          <span className="text-sm text-gray-900">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 border-t border-gray-900/10 pt-4">
                      <div>
                        <div className="text-lg font-bold text-gray-900">{cardDetails.croitre.stats.pays}</div>
                        <div className="text-xs text-gray-900/60">{t('home.objectif.croitre.stats.paysLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{cardDetails.croitre.stats.collaborations}</div>
                        <div className="text-xs text-gray-900/60">{t('home.objectif.croitre.stats.collaborationsLabel')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{cardDetails.croitre.stats.impact}</div>
                        <div className="text-xs text-gray-900/60">{t('home.objectif.croitre.stats.impactLabel')}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// HeroTextSnippet Component
export function HeroTextSnippet() {
  return (
    <div className="flex flex-col items-end gap-4">
      {/* FIND with mint highlight */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        className="relative"
      >
        <div className="relative inline-block">
          <span className="relative text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">FIN</span>
          <span className="relative text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">
            D
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
              className="absolute right-0 top-1/2 -translate-y-1/2 h-[70%] w-[140%] bg-[#66FFCC] -z-10 origin-right"
              style={{ scaleX: 1 }}
            />
          </span>
        </div>
      </motion.div>

      {/* LEARN with pink highlight */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        className="relative mr-8 md:mr-16"
      >
        <div className="relative inline-block">
          <span className="relative text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">LEA</span>
          <span className="relative text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">
            RN
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
              className="absolute right-0 top-1/2 -translate-y-1/2 h-[70%] w-[140%] bg-[#FF2E7F] -z-10 origin-right"
              style={{ scaleX: 1 }}
            />
          </span>
        </div>
      </motion.div>

      {/* EXCEL with yellow highlight */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
        className="relative mr-16 md:mr-32"
      >
        <div className="relative inline-block">
          <span className="relative text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">EX</span>
          <span className="relative text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">
            CEL
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.9, ease: "easeOut" }}
              className="absolute right-0 top-1/2 -translate-y-1/2 h-[70%] w-[140%] bg-[#FFDE59] -z-10 origin-right"
              style={{ scaleX: 1 }}
            />
          </span>
        </div>
      </motion.div>
    </div>
  )
}

// HeroSlider Component
const HeroSlider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slides, setSlides] = useState<SliderItem[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const data = await slidersService.getPublicSliders();
        setSlides(data);
      } catch (error) {
        console.error("Failed to fetch sliders", error);
      }
    };
    fetchSlides();
  }, []);

  const totalSlides = slides.length + 1;

  useEffect(() => {
    if (totalSlides <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(interval);
  }, [totalSlides, isPaused]);

  const nextSlide = () => setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentSlideIndex((prev) => (prev - 1 + totalSlides) % totalSlides);

  const getVariantStyles = (variant: SliderColorVariant) => {
    switch (variant) {
      case 'mint':
        return { bg: 'bg-brand-mint', gradient: 'from-brand-mint/40 to-brand-mint/10', btn: 'bg-brand-mint text-black hover:bg-[#4DD4B8]' };
      case 'yellow':
        return { bg: 'bg-brand-accent', gradient: 'from-brand-accent/40 to-brand-accent/10', btn: 'bg-brand-accent text-black hover:bg-[#E6C547]' };
      case 'purple':
        return { bg: 'bg-brand-primary', gradient: 'from-brand-primary/40 to-brand-primary/10', btn: 'bg-brand-primary text-white hover:bg-[#6B1A9E]' };
      case 'pink':
      default:
        return { bg: 'bg-brand-secondary', gradient: 'from-brand-secondary/40 to-brand-secondary/10', btn: 'bg-brand-secondary text-white hover:bg-[#E0266B]' };
    }
  };

  const currentSlide = currentSlideIndex > 0 ? slides[currentSlideIndex - 1] : null;
  const currentStyles = currentSlide ? getVariantStyles(currentSlide.color_variant) : null;

  return (
    <section 
      className="relative overflow-hidden w-full min-h-screen flex items-center group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        {currentSlideIndex === 0 ? (
          <motion.div
            key="hero-slide-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 w-full h-full z-10"
          >
            {children}
          </motion.div>
        ) : currentSlide && currentStyles ? (
          <motion.div
            key={`hero-slide-${currentSlide.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 w-full h-full flex items-center justify-center z-10"
          >
            {currentSlide.image_url ? (
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
                style={{ backgroundImage: `url(${API_ENDPOINTS.UPLOADS}/${currentSlide.image_url})` }} 
              />
            ) : (
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
                style={{ backgroundImage: `url(${heroBg})` }} 
              />
            )}
            <div className={`absolute inset-0 bg-gradient-to-br ${currentStyles.gradient} backdrop-blur-[2px]`} />
            <div className="absolute inset-0 bg-black/50" />

            <div className="relative w-full max-w-4xl mx-auto px-4 z-20 text-center">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight drop-shadow-xl"
                  style={{ fontFamily: 'Gabarito, Inter, sans-serif' }}>
                {currentSlide.title}
              </h2>
              {currentSlide.subtitle && (
                <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-10 font-medium max-w-2xl mx-auto drop-shadow-md">
                  {currentSlide.subtitle}
                </p>
              )}
              <Link
                to={currentSlide.button_url}
                className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-bold text-sm md:text-lg transition-all transform hover:scale-105 shadow-xl ${currentStyles.btn}`}
              >
                {currentSlide.button_label}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Navigation Overlays */}
      {totalSlides > 1 && (
        <div className="absolute inset-0 pointer-events-none z-30">
          <button 
            onClick={prevSlide}
            className="pointer-events-auto absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition-all hidden md:block opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
          </button>
          <button 
            onClick={nextSlide}
            className="pointer-events-auto absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition-all hidden md:block opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
          </button>

          <div className="pointer-events-auto absolute bottom-8 left-0 right-0 flex justify-center gap-2 md:gap-3">
            {Array.from({ length: totalSlides }).map((_, idx) => {
              const isCurrent = idx === currentSlideIndex;
              let dotClass = 'w-2 md:w-3 bg-white/50 hover:bg-white/80';
              if (isCurrent) {
                if (idx === 0) dotClass = 'w-8 md:w-12 bg-white';
                else {
                  const style = getVariantStyles(slides[idx - 1].color_variant);
                  dotClass = `w-8 md:w-12 ${style.bg}`;
                }
              }
              return (
                <button
                  key={idx}
                  onClick={() => setCurrentSlideIndex(idx)}
                  className={`h-2 md:h-3 rounded-full transition-all duration-300 ${dotClass}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [featuredTrainings, setFeaturedTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(2); // Default: 2 cards per row

  // Calendar state
  const [calendarSessions, setCalendarSessions] = useState<Session[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'>('dayGridMonth');
  const [hoveredEvent, setHoveredEvent] = useState<any>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [hoveredEventColor, setHoveredEventColor] = useState<string>('#66FFCC');
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState<ContactFormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');


  useEffect(() => {
    fetchFeaturedTrainings();
    fetchCalendarSessions();

    // Check if mobile and update cards per view
    const checkResponsive = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);

      // Update cards per view based on screen size - always show 2 cards per row on desktop/tablet
      if (width < 768) {
        setCardsPerView(1); // Mobile: 1 card
      } else {
        setCardsPerView(2); // Tablet/Desktop: 2 cards per row
      }
    };

    checkResponsive();
    window.addEventListener('resize', checkResponsive);

    return () => {
      window.removeEventListener('resize', checkResponsive);
    };
  }, []);

  // Reset slide index when cardsPerView changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [cardsPerView]);

  // Auto-slide functionality
  useEffect(() => {
    const featuredTrainingsList = featuredTrainings.filter(training => training.is_featured);
    const totalSlides = Math.max(0, featuredTrainingsList.length - cardsPerView) + 1;

    if (totalSlides > 1 && featuredTrainingsList.length > cardsPerView) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => {
          const maxSlide = Math.max(0, featuredTrainingsList.length - cardsPerView);
          return prev < maxSlide ? prev + 1 : 0;
        });
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }
  }, [featuredTrainings, cardsPerView]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);


  const fetchFeaturedTrainings = async () => {
    try {
      setLoading(true);
      const data = await trainingsService.getFeaturedTrainings();
      setFeaturedTrainings(data);
    } catch (err) {
      setError(t('common.error'));
      // Don't redirect on error, just show empty state
      setFeaturedTrainings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarSessions = async () => {
    try {
      setCalendarLoading(true);
      const sessions = await sessionsService.getAllSessions();
      setCalendarSessions(sessions);

      // Transform session data into FullCalendar events
      const events = sessions.map((session, index) => {
        // Parse date string as local date to avoid timezone issues
        const [year, month, day] = session.session_date.split('-').map(Number);
        const startDate = new Date(year, month - 1, day, 9, 0, 0); // 9 AM start time
        const endDate = new Date(startDate);
        endDate.setHours(startDate.getHours() + 8); // Default 8-hour session

        // Use brand colors in rotation
        const colors = [
          { bg: '#FF2E7F', text: '#FFFFFF' }, // Brand Pink
          { bg: '#7D20BE', text: '#FFFFFF' }, // Brand Purple
          { bg: '#66FFCC', text: '#000000' }, // Brand Mint
          { bg: '#FFDE59', text: '#000000' }, // Brand Yellow
        ];
        const colorIndex = index % colors.length;

        return {
          id: session.id,
          title: session.training.title,
          start: startDate,
          end: endDate,
          backgroundColor: colors[colorIndex].bg,
          borderColor: 'transparent',
          textColor: colors[colorIndex].text,
          className: `gradient-event-${colorIndex}`,
          extendedProps: {
            center: session.training.training_center.name,
            location: formatTrainingLocation(session.training as unknown as Training),
            price: session.training.pricing_dzd,
            duration: session.training.duration,
            format: session.training.format,
            specialties: session.training.domaine ? [session.training.domaine.name_french] : [],
            description: session.training.short_description || session.training.full_description,
            trainer: session.training.trainer,
            level: session.training.level,
            sessionDate: session.session_date,
            trainingId: session.training.id,
          }
        };
      });

      setCalendarEvents(events);
    } catch (err) {
      // Don't redirect on error, just show empty calendar
      setCalendarSessions([]);
      setCalendarEvents([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  // FullCalendar event handlers
  const handleEventClick = (clickInfo: any) => {
    const event = clickInfo.event;
    const extendedProps = event.extendedProps;

    // Navigate to training detail page using the training ID
    window.location.href = `/training/${extendedProps.trainingId}`;
  };

  const handleEventMouseEnter = (mouseEnterInfo: any) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    const event = mouseEnterInfo.event;
    const extendedProps = event.extendedProps;

    // Get the event color based on its className
    let eventColor = '#66FFCC'; // Default mint color
    if (event.classNames.includes('gradient-event-0')) {
      eventColor = '#FF2E7F'; // Pink
    } else if (event.classNames.includes('gradient-event-1')) {
      eventColor = '#7D20BE'; // Purple
    } else if (event.classNames.includes('gradient-event-2')) {
      eventColor = '#66FFCC'; // Mint
    } else if (event.classNames.includes('gradient-event-3')) {
      eventColor = '#FFDE59'; // Yellow
    }

    setHoveredEventColor(eventColor);

    setHoveredEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      ...extendedProps
    });

    // Get mouse position for card positioning
    const rect = mouseEnterInfo.el.getBoundingClientRect();
    setHoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleEventMouseLeave = () => {
    // Add a delay before hiding the card
    const timeout = setTimeout(() => {
      setHoveredEvent(null);
    }, 300); // 300ms delay
    setHoverTimeout(timeout);
  };

  const handleCardMouseEnter = () => {
    // Clear timeout when hovering over the card
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleCardMouseLeave = () => {
    // Hide the card when leaving the card itself
    setHoveredEvent(null);
  };

  const handleDateSelect = (selectInfo: any) => {
    // Handle date selection if needed
  };

  const handleViewChange = (view: any) => {
    setViewMode(view.view.type);
  };

  // Contact form handlers
  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setSubmitStatus('error');
      setSubmitMessage(t('home.contact.fillAllFields'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      const result = await contactService.sendMessage(contactForm);

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage(t('home.contact.successMessage'));

        // Reset form
        setContactForm({
          name: '',
          email: '',
          message: ''
        });

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
          setSubmitMessage('');
        }, 5000);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(t('home.contact.errorMessage'));
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setSubmitMessage(error.message || t('home.contact.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const stats = [
    { icon: GraduationCap, value: '500+', label: t('home.stats.specializedTrainings'), desc: t('home.stats.catalogComplete') },
    { icon: Building2, value: '50+', label: t('home.stats.partnerCenters'), desc: t('home.stats.nationalNetwork') },
    { icon: Users, value: '2000+', label: t('home.stats.trainedProfessionals'), desc: t('home.stats.recognizedExcellence') },
    { icon: Star, value: '4.8/5', label: t('home.stats.clientSatisfaction'), desc: t('home.stats.guaranteedQuality') },
  ];

  const specialites = [
    { name: t('home.specialties.qualityAssurance'), code: 'AQ', icon: Shield, color: 'from-brand-primary to-purple-600', badge: 'Popular', description: 'Formations en contrôle qualité et assurance' },
    { name: t('home.specialties.regulatoryAffairs'), code: 'AR', icon: BookOpen, color: 'from-brand-secondary to-pink-600', badge: 'New', description: 'Conformité et réglementation pharmaceutique' },
    { name: t('home.specialties.goodPractices'), code: 'BP', icon: CheckCircle, color: 'from-brand-mint to-teal-600', badge: 'Trending', description: 'Standards et procédures de qualité' },
    { name: t('home.specialties.qualityControl'), code: 'CQ', icon: Target, color: 'from-brand-accent to-yellow-600', badge: 'Free', description: 'Techniques de contrôle et validation' },
    { name: 'Pharmacovigilance', code: 'PV', icon: Heart, color: 'from-brand-secondary to-pink-600', badge: 'Hot', description: 'Surveillance des effets indésirables' },
    { name: 'Validation', code: 'VAL', icon: Zap, color: 'from-brand-primary to-purple-600', badge: 'Premium', description: 'Validation des processus et équipements' },
  ];

  const avantagesCles = [
    {
      icon: Shield,
      titre: 'Certifications reconnues',
      description: 'Formations accréditées par les autorités sanitaires algériennes et internationales',
      highlight: 'ISO 9001',
      color: 'from-brand-primary to-purple-600',
    },
    {
      icon: Award,
      titre: 'Expertise sectorielle',
      description: 'Intervenants seniors avec 15+ ans d\'expérience en industrie pharmaceutique',
      highlight: '95% praticiens',
      color: 'from-brand-secondary to-pink-600',
    },
    {
      icon: Zap,
      titre: 'Formats innovants',
      description: 'Apprentissage mixte combinant théorie, cas pratiques et simulations',
      highlight: 'Blended learning',
      color: 'from-brand-mint to-teal-600',
    },
    {
      icon: Target,
      titre: 'Suivi personnalisé',
      description: t('home.objectif.trouver.description'),
      highlight: '6 mois suivi',
      color: 'from-brand-accent to-yellow-600',
    },
  ];

  const getAvailabilityStatus = (participants: number, max: number) => {
    const ratio = participants / max;
    if (ratio >= 0.9) return { text: 'Complet bientôt', color: 'bg-red-50 text-red-700 border-red-200' };
    if (ratio >= 0.7) return { text: 'Places limitées', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    return { text: 'Places disponibles', color: 'bg-green-50 text-green-700 border-green-200' };
  };

  return (
    <div className="min-h-screen bg-brand-surface">
      {/* Custom FullCalendar Styles */}
      <style>{`
        .fullcalendar-container {
          --fc-button-bg-color: #FF2E7F;
          --fc-button-border-color: #FF2E7F;
          --fc-button-hover-bg-color: #E0266B;
          --fc-button-hover-border-color: #E0266B;
          --fc-button-active-bg-color: #CC1E5A;
          --fc-button-active-border-color: #CC1E5A;
          --fc-button-text-color: #FFFFFF;
          --fc-today-bg-color: #FF2E7F20;
          --fc-border-color: #E5E7EB;
          --fc-daygrid-event-bg-color: #FF2E7F;
          --fc-daygrid-event-border-color: #FF2E7F;
          --fc-event-bg-color: #FF2E7F;
          --fc-event-border-color: #FF2E7F;
          --fc-highlight-color: #FF2E7F20;
          --fc-more-link-bg-color: #7D20BE;
          --fc-more-link-border-color: #7D20BE;
          --fc-more-link-color: #FFFFFF;
          --fc-neutral-bg-color: #F9FAFB;
          --fc-page-bg-color: #FFFFFF;
          --fc-today-bg-color: #FF2E7F20;
        }
        
        .fc-toolbar {
          gap: 8px !important;
        }
        
        .fc-toolbar-chunk {
          display: flex !important;
          gap: 8px !important;
        }
        
        /* Mobile Calendar Styles */
        .mobile-calendar .fc-toolbar {
          flex-wrap: wrap !important;
          gap: 4px !important;
        }
        
        .mobile-calendar .fc-toolbar-chunk {
          gap: 4px !important;
        }
        
        .mobile-calendar .fc-button {
          padding: 4px 8px !important;
          font-size: 12px !important;
          margin: 0 2px !important;
        }
        
        .mobile-calendar .fc-toolbar-title {
          font-size: 1.2rem !important;
          margin: 4px 0 !important;
        }
        
        .fc-button {
          border-radius: 12px !important;
          font-weight: 600 !important;
          text-transform: none !important;
          padding: 6px 12px !important;
          font-size: 13px !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
          border: none !important;
          margin: 0 4px !important;
        }
        
        .fc-button:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
        }
        
        /* Navigation buttons (prev/next) */
        .fc-button-primary {
          background-color: #FF2E7F !important;
        }
        
        .fc-button-primary:hover {
          background-color: #E0266B !important;
        }
        
        .fc-button-primary:focus {
          box-shadow: 0 0 0 3px rgba(255, 46, 127, 0.3) !important;
        }
        
        /* Today button */
        .fc-today-button {
          background-color: #66FFCC !important;
          color: #000000 !important;
        }
        
        .fc-today-button:hover {
          background-color: #4DD4B8 !important;
        }
        
        /* View mode buttons */
        .fc-dayGridMonth-button {
          background-color: #FF2E7F !important;
        }
        
        .fc-dayGridMonth-button:hover {
          background-color: #E0266B !important;
        }
        
        .fc-dayGridMonth-button.fc-button-active {
          background-color: #FF2E7F !important;
          box-shadow: 0 0 0 2px rgba(255, 46, 127, 0.3) !important;
        }
        
        .fc-timeGridWeek-button {
          background-color: #7D20BE !important;
        }
        
        .fc-timeGridWeek-button:hover {
          background-color: #6B1A9E !important;
        }
        
        .fc-timeGridWeek-button.fc-button-active {
          background-color: #7D20BE !important;
          box-shadow: 0 0 0 2px rgba(125, 32, 190, 0.3) !important;
        }
        
        .fc-timeGridDay-button {
          background-color: #FFDE59 !important;
          color: #000000 !important;
        }
        
        .fc-timeGridDay-button:hover {
          background-color: #E6C547 !important;
        }
        
        .fc-timeGridDay-button.fc-button-active {
          background-color: #FFDE59 !important;
          box-shadow: 0 0 0 2px rgba(255, 222, 89, 0.3) !important;
        }
        
        .fc-daygrid-day-number {
          color: #374151 !important;
          font-weight: 500 !important;
        }
        
        .fc-day-today .fc-daygrid-day-number {
          background-color: #FF2E7F !important;
          color: #FFFFFF !important;
          border-radius: 50% !important;
          width: 24px !important;
          height: 24px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        .fc-daygrid-event {
          border-radius: 8px !important;
          border: none !important;
          margin: 2px 0 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
        
        .fc-event-title {
          font-weight: 600 !important;
          font-size: 12px !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
        }
        
        .fc-event-time {
          font-size: 11px !important;
          opacity: 0.9 !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
        }
        
        .fc-daygrid-event-harness {
          margin: 1px 0 !important;
        }
        
        .fc-daygrid-day-frame {
          min-height: 120px !important;
        }
        
        .fc-daygrid-day-events {
          margin: 4px 0 !important;
        }
        
        .fc-daygrid-day-top {
          padding: 8px !important;
        }
        
        .fc-col-header-cell {
          background-color: #F3F4F6 !important;
          color: #374151 !important;
          font-weight: 600 !important;
          padding: 12px 8px !important;
        }
        
        .fc-daygrid-day {
          border-color: #E5E7EB !important;
        }
        
        .fc-daygrid-day:hover {
          background-color: #F9FAFB !important;
        }
        
        .fc-timegrid-slot {
          border-color: #E5E7EB !important;
        }
        
        .fc-timegrid-axis {
          color: #6B7280 !important;
        }
        
        .fc-timegrid-event {
          border-radius: 6px !important;
          border: none !important;
        }
        
        .fc-popover {
          border-radius: 12px !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #E5E7EB !important;
        }
        
        .fc-popover-header {
          background-color: #FF2E7F !important;
          color: #FFFFFF !important;
          border-radius: 12px 12px 0 0 !important;
          padding: 12px 16px !important;
        }
        
        .fc-popover-body {
          padding: 16px !important;
        }
        
        .training-event {
          border-radius: 6px !important;
          border: none !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
        }
        
        .training-event-content {
          padding: 4px 8px !important;
        }
        
        .fc-more-link {
          background-color: #7D20BE !important;
          color: #FFFFFF !important;
          border-radius: 6px !important;
          padding: 3px 8px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
        }
        
        .fc-more-link:hover {
          background-color: #6B1A9E !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
        }
        
        .fc-daygrid-more-link {
          color: #7D20BE !important;
          font-weight: 600 !important;
        }
        
        .fc-daygrid-more-link:hover {
          color: #6B1A9E !important;
        }
        
        .fc-toolbar-title {
          color: #FF2E7F !important;
          font-weight: 700 !important;
          font-size: 1.5rem !important;
        }
        
        /* Event Classes with Brand Colors */
        .gradient-event-0 {
          background-color: #FF2E7F !important;
        }
        
        .gradient-event-1 {
          background-color: #7D20BE !important;
        }
        
        .gradient-event-2 {
          background-color: #66FFCC !important;
          color: #000000 !important;
        }
        
        .gradient-event-3 {
          background-color: #FFDE59 !important;
          color: #000000 !important;
        }
        
        .fc-daygrid-day-number:hover {
          background-color: #F3F4F6 !important;
          border-radius: 50% !important;
          width: 24px !important;
          height: 24px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        /* Mobile Calendar Styles */
        .mobile-calendar .fc-header-toolbar {
          padding: 0.5rem;
          margin-bottom: 0.5rem;
        }
        
        .mobile-calendar .fc-toolbar-title {
          font-size: 1rem;
          font-weight: 600;
        }
        
        .mobile-calendar .fc-button {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
        }
        
        .mobile-calendar .fc-daygrid-day {
          min-height: 60px;
        }
        
        .mobile-calendar .fc-daygrid-day-number {
          font-size: 0.75rem;
          padding: 0.25rem;
        }
        
        .mobile-calendar .fc-daygrid-day-frame {
          padding: 0.125rem;
        }
        
        .mobile-calendar .fc-event {
          font-size: 0.625rem;
          padding: 0.125rem;
          margin: 0.0625rem;
        }
        
        .mobile-calendar .fc-daygrid-event {
          margin: 0.0625rem 0;
        }
        
        .mobile-calendar .fc-daygrid-more-link {
          font-size: 0.625rem;
        }
        
        .mobile-calendar .fc-col-header-cell {
          padding: 0.25rem 0;
          font-size: 0.75rem;
        }
        
        .mobile-calendar .fc-scrollgrid {
          border: none;
        }
        
        .mobile-calendar .fc-scrollgrid-sync-table {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
        }
        
        .mobile-calendar .fc-daygrid-day-events {
          margin: 0;
        }
      `}</style>

      {/* Import Gabarito Font */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Gabarito:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Additional CSS to ensure buttons are clickable */}
      <style>{`
        .hero-text-persistent {
          opacity: 1 !important;
          visibility: visible !important;
          pointer-events: auto !important;
        }
        .hero-text-persistent * {
          opacity: 1 !important;
          visibility: visible !important;
          pointer-events: auto !important;
        }
        .hero-buttons {
          position: relative !important;
          z-index: 999 !important;
          pointer-events: auto !important;
        }
        .hero-buttons a {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
      `}</style>

      {/* Hero Section - Wrapped in HeroSlider */}
      <HeroSlider>
        <section className="relative overflow-hidden min-h-screen w-full flex items-center">
        {/* Background Image with Parallax Effect */}
        <motion.div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${heroBg})`
          }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />

        {/* Clean Overlay - Reduced Intensity */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/50 via-brand-primary/40 to-brand-primary/30" />

        {/* Subtle Geometric Elements - Hidden on Mobile */}
        <div className="absolute inset-0 overflow-hidden hidden lg:block">
          <motion.div
            className="absolute top-20 left-20 w-20 h-20 bg-brand-mint/15 rounded-full"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-28 h-28 bg-brand-secondary/15 rounded-full"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
          />
          <motion.div
            className="absolute top-1/2 right-1/4 w-16 h-16 bg-brand-accent/15 rounded-full"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.9, ease: "easeOut" }}
          />
        </div>

        {/* Main Container - 80% width */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-4/5 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center min-h-screen py-16 md:py-24 lg:py-32">

            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-center lg:text-left order-2 lg:order-1"
            >

              {/* Welcome Text with Two-Phase Animation */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="text-center lg:text-left mb-8 md:mb-12 lg:mb-16"
              >
                {/* Main Welcome Text */}
                <motion.h1
                  initial={{ opacity: 0, y: 40, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight mb-2 md:mb-3 tracking-tight"
                  style={{
                    fontFamily: 'Gabarito, Inter, system-ui, -apple-system, sans-serif',
                    fontWeight: 800,
                    letterSpacing: '-0.02em'
                  }}
                >
                  <motion.span
                    className="block"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 5.0 }}
                  >
                    {t('public.home.hero.title').split(' – ')[0]}
                  </motion.span>
                  <span className="block bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent">
                    PHINLEX
                  </span>
                </motion.h1>

                {/* Text Animation Container - Fixed Height */}
                <div className="relative h-20 md:h-24 lg:h-28 xl:h-32 flex items-center justify-center lg:justify-start">
                  {/* Phase 1: Question Text */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                    className="absolute inset-0 flex items-center justify-center lg:justify-start"
                  >
                    <motion.p
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.5, delay: 4.5 }}
                      className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-white/90 font-medium leading-relaxed max-w-4xl mx-auto lg:mx-0 text-center lg:text-left"
                      style={{
                        fontFamily: 'Gabarito, Inter, system-ui, -apple-system, sans-serif',
                        fontWeight: 500,
                        letterSpacing: '0.01em'
                      }}
                    >
                      {t('home.hero.question')}
                    </motion.p>
                  </motion.div>

                  {/* Phase 2: Answer Text */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, rotateX: 15 }}
                    animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                    transition={{
                      duration: 1.2,
                      delay: 5.0,
                      ease: [0.25, 0.46, 0.45, 0.94],
                      type: "spring",
                      stiffness: 100,
                      damping: 15
                    }}
                    className="absolute inset-0 flex items-center justify-center lg:justify-start"
                  >
                    <motion.p
                      className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-white/90 font-medium leading-relaxed max-w-4xl mx-auto lg:mx-0 text-center lg:text-left"
                      style={{
                        fontFamily: 'Gabarito, Inter, system-ui, -apple-system, sans-serif',
                        fontWeight: 500,
                        letterSpacing: '0.01em'
                      }}
                    >
                      <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 5.2 }}
                      >
                        {t('home.hero.answer')}
                      </motion.span>
                    </motion.p>
                  </motion.div>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center lg:justify-start"
              >
                {/* Primary CTA */}
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  className="hero-text-persistent hero-buttons"
                >
                  <Link
                    to="/trainings"
                    className="group inline-flex h-9 sm:h-10 md:h-12 lg:h-14 items-center justify-center rounded-xl sm:rounded-2xl px-3 sm:px-4 md:px-6 lg:px-8 font-semibold text-white bg-gradient-to-r from-brand-secondary to-pink-600 hover:from-pink-600 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl text-xs sm:text-xs md:text-sm lg:text-base min-w-[140px] sm:min-w-[160px] md:min-w-[180px] lg:min-w-[200px] relative z-20"
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  >
                    <div className="flex items-center">
                      <GraduationCap className="mr-1 sm:mr-2 md:mr-3 h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 lg:h-6 lg:w-6" />
                      {t('public.home.hero.exploreTrainings')}
                      <ArrowUpRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-3 sm:w-3 md:h-4 md:w-4 lg:h-5 lg:w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>

                {/* Secondary CTA */}
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  className="hero-text-persistent hero-buttons"
                >
                  <Link
                    to="/auth/role-selection"
                    className="group inline-flex h-9 sm:h-10 md:h-12 lg:h-14 items-center justify-center rounded-xl sm:rounded-2xl px-3 sm:px-4 md:px-6 lg:px-8 font-semibold border-2 border-white/40 text-white hover:bg-white/10 hover:border-white/60 transition-all duration-300 text-xs sm:text-xs md:text-sm lg:text-base min-w-[140px] sm:min-w-[160px] md:min-w-[180px] lg:min-w-[200px] relative z-20"
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  >
                    <div className="flex items-center">
                      <Building2 className="mr-1 sm:mr-2 md:mr-3 h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 lg:h-6 lg:w-6" />
                      {t('public.home.hero.joinAsCenter')}
                    </div>
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Right Side - FIND/LEARN/EXCEL - 80% width */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
              className="flex items-center justify-center lg:justify-end order-1 lg:order-2 mb-8 lg:mb-0"
            >
              <div className="w-4/5 max-w-lg lg:max-w-none flex flex-col items-center lg:items-end gap-1 md:gap-2 lg:gap-3 xl:gap-4">
                {/* FIND with mint highlight */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.4, ease: "easeOut" }}
                  className="relative w-full text-center lg:text-right"
                >
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-bold text-white leading-none tracking-tight">
                    <span className="relative inline-block">
                      FIN
                      <span className="relative inline-block">
                        <span className="relative z-10">D</span>
                        <motion.span
                          className="absolute inset-0 bg-brand-mint -z-10 -mx-2 md:-mx-3 lg:-mx-4 -my-1 md:-my-2"
                          initial={{ scaleX: 0, originX: 1 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.7, delay: 1.1, ease: "easeOut" }}
                        />
                      </span>
                    </span>
                  </h1>
                </motion.div>

                {/* LEARN with pink highlight */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.6, ease: "easeOut" }}
                  className="relative w-full text-center lg:text-right -mr-4 md:-mr-6 lg:-mr-8 xl:-mr-12"
                >
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-bold text-white leading-none tracking-tight">
                    <span className="relative inline-block">
                      <span className="relative inline-block">
                        <span className="relative z-10">LEARN</span>
                        <motion.span
                          className="absolute inset-0 bg-brand-secondary -z-10 -mx-2 md:-mx-3 lg:-mx-4 -my-1 md:-my-2"
                          initial={{ scaleX: 0, originX: 1 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.7, delay: 1.3, ease: "easeOut" }}
                        />
                      </span>
                    </span>
                  </h1>
                </motion.div>

                {/* EXCEL with yellow highlight */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.8, ease: "easeOut" }}
                  className="relative w-full text-center lg:text-right"
                >
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-bold text-white leading-none tracking-tight">
                    <span className="relative inline-block">
                      EX
                      <span className="relative inline-block">
                        <span className="relative z-10">CEL</span>
                        <motion.span
                          className="absolute inset-0 bg-brand-accent -z-10 -mx-2 md:-mx-3 lg:-mx-4 -my-1 md:-my-2"
                          initial={{ scaleX: 0, originX: 1 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.7, delay: 1.5, ease: "easeOut" }}
                        />
                      </span>
                    </span>
                  </h1>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
        </section>
      </HeroSlider>

      {/* PHINLEX Bridge Section - Optimized for Small Screens */}
      <section id="phinlex-bridge-section" className="py-12 md:py-16 lg:py-20 xl:py-24 bg-gradient-to-br from-brand-surface via-white to-purple-50 relative overflow-hidden">

        {/* Background Pattern - Subtle */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221.5%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-60" />

        {/* Floating Elements - Hidden on Mobile */}
        <div className="absolute inset-0 overflow-hidden hidden lg:block">
          <motion.div
            className="absolute top-20 left-20 w-20 h-20 bg-gradient-to-br from-brand-primary/15 to-brand-secondary/15 rounded-full blur-xl"
            animate={{
              y: [0, -15, 0],
              x: [0, 8, 0],
              scale: [1, 1.05, 1]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-24 h-24 bg-gradient-to-br from-brand-secondary/15 to-brand-mint/15 rounded-full blur-xl"
            animate={{
              y: [0, 15, 0],
              x: [0, -8, 0],
              scale: [1, 0.95, 1]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute top-1/2 right-1/4 w-16 h-16 bg-gradient-to-br from-brand-mint/15 to-brand-accent/15 rounded-full blur-xl"
            animate={{
              y: [0, -10, 0],
              x: [0, 12, 0],
              scale: [1, 1.02, 1]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Main Container - 80% width */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-4/5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 xl:gap-16 items-center">

            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-center lg:text-left space-y-4 md:space-y-6"
            >

              {/* Main Headline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-3"
              >
                <h2
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight"
                  style={{
                    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                    fontWeight: 800,
                    letterSpacing: '-0.02em'
                  }}
                >
                  <span className="block">{t('home.bridge.title')}</span>
                  <span className="block bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-accent bg-clip-text text-transparent">
                    {t('home.bridge.subtitle')}
                  </span>
                </h2>
              </motion.div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                style={{
                  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '0.01em'
                }}
              >
                {t('home.bridge.description')}
              </motion.p>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="pt-2"
              >
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link
                    to="/trainings"
                    className="group inline-flex h-10 md:h-12 lg:h-14 items-center justify-center rounded-xl md:rounded-2xl px-4 md:px-6 lg:px-8 font-semibold text-white bg-gradient-to-r from-brand-secondary via-pink-500 to-pink-600 hover:from-pink-600 hover:via-pink-500 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl text-xs sm:text-sm md:text-base lg:text-lg w-full sm:w-auto min-w-[160px] md:min-w-[180px] lg:min-w-[200px]"
                  >
                    <div className="flex items-center">
                      <BookOpen className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 lg:h-5 lg:w-5" />
                      {t('home.bridge.catalogButton')}
                      <ArrowUpRight className="ml-1 md:ml-2 h-2 w-2 md:h-3 md:w-3 lg:h-4 lg:w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Right Side - Optimized Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
              className="relative mt-6 lg:mt-0"
            >
              {/* Main Card - Fixed Aspect Ratio */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, rotateY: 10 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.6 }}
                className="relative bg-white/10 backdrop-blur-xl rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 border border-white/20 shadow-2xl min-h-[280px] md:min-h-[320px] lg:min-h-[360px]"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg">
                      <Search className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm md:text-base lg:text-lg font-bold text-gray-900">{t('home.bridge.card.title')}</h3>
                      <p className="text-xs md:text-sm text-gray-600">{t('home.bridge.card.subtitle')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg md:text-xl lg:text-2xl font-bold text-brand-primary">95%</div>
                    <div className="text-xs text-gray-500">{t('home.bridge.card.precision')}</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4 md:mb-6">
                  <div className="flex justify-between text-xs md:text-sm text-gray-600 mb-2">
                    <span>{t('home.bridge.card.efficiency')}</span>
                    <span className="font-semibold">95%</span>
                  </div>
                  <div className="w-full bg-gray-200/50 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "95%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, delay: 1.2 }}
                      className="bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-accent h-2 rounded-full shadow-sm"
                    />
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2 md:space-y-3">
                  {[
                    { icon: Filter, text: t('home.bridge.card.features.advancedFilters'), color: "text-brand-mint" },
                    { icon: Star, text: t('home.bridge.card.features.userReviews'), color: "text-brand-accent" },
                    { icon: Target, text: t('home.bridge.card.features.recommendations'), color: "text-brand-primary" }
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
                      className="flex items-center space-x-2 md:space-x-3"
                    >
                      <div className={`w-6 h-6 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm`}>
                        <feature.icon className={`h-3 w-3 md:h-4 md:w-4 ${feature.color}`} />
                      </div>
                      <span className="text-xs md:text-sm lg:text-base text-gray-700 font-medium">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Floating Stats Cards - Hidden on Mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -top-3 md:-top-4 -right-3 md:-right-4 bg-white/20 backdrop-blur-lg rounded-xl md:rounded-2xl p-2 md:p-3 border border-white/30 shadow-lg hidden sm:block"
              >
                <div className="flex items-center space-x-1 md:space-x-2">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-brand-mint to-brand-accent rounded-lg flex items-center justify-center shadow-sm">
                    <TrendingUp className="h-2 w-2 md:h-3 md:w-3 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-900">150+</div>
                    <div className="text-xs text-gray-500">{t('home.bridge.card.stats.trainings')}</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="absolute -bottom-3 md:-bottom-4 -left-3 md:-left-4 bg-white/20 backdrop-blur-lg rounded-xl md:rounded-2xl p-2 md:p-3 border border-white/30 shadow-lg hidden sm:block"
              >
                <div className="flex items-center space-x-1 md:space-x-2">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center shadow-sm">
                    <Users className="h-2 w-2 md:h-3 md:w-3 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-900">25+</div>
                    <div className="text-xs text-gray-500">{t('home.bridge.card.stats.centers')}</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Objectif Section */}
      <ScrollReveal delay={0.1}>
        <ObjectifSection />
      </ScrollReveal>

      {/* Featured Trainings Section */}
      <ScrollReveal delay={0.2}>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">

              {/* Left Side - Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                {/* Main Headline */}
                <div className="space-y-4">
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
                  >
                    <span className="text-brand-secondary">{t('home.featured.title')}</span>
                  </motion.h2>
                </div>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-lg text-gray-600 max-w-2xl"
                >
                  {t('home.featured.subtitle')}
                </motion.p>

                {/* Call to Action Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Link
                    to="/auth/role-selection"
                    className="group inline-flex items-center justify-center px-8 py-4 bg-brand-primary hover:bg-purple-700 text-white font-semibold text-lg rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {t('home.featured.ctaButton')}

                  </Link>
                </motion.div>
              </motion.div>

              {/* Right Side - Featured Trainings Slider */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative lg:col-span-2"
              >
                {loading ? (
                  <div className="flex gap-4 md:gap-6">
                    {Array.from({ length: 2 }).map((_, index) => (
                      <div
                        key={index}
                        className={`${index === 0 ? 'w-full md:w-1/2' : 'hidden md:block md:w-1/2'} h-96 bg-gray-200 rounded-2xl animate-pulse`}
                      />
                    ))}
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                      onClick={fetchFeaturedTrainings}
                      className="bg-brand-secondary text-white px-6 py-3 rounded-xl hover:bg-pink-700 transition-colors"
                    >
                      Réessayer
                    </button>
                  </div>
                ) : (() => {
                  const featuredList = featuredTrainings.filter(training => training.is_featured);
                  return featuredList.length > 0;
                })() ? (
                  <div className="relative w-full">
                    <div className="relative overflow-hidden rounded-2xl">
                      {/* Slider Container */}
                      <div className="relative">
                        <div className="overflow-hidden">
                          <motion.div
                            animate={{ x: `-${currentSlide * (100 / cardsPerView)}%` }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="flex"
                          >
                            {(() => {
                              const featuredList = featuredTrainings.filter(t => t.is_featured);
                              return featuredList.map((training, index) => (
                                <div
                                  key={training.id}
                                  className={`flex-shrink-0 ${cardsPerView === 1 ? 'w-full' : 'w-1/2'} px-3`}
                                >
                                  <Link
                                    to={`/training/${training.id}`}
                                    className="block w-full"
                                  >
                                    <motion.div
                                      initial={{ opacity: 0, y: 20 }}
                                      whileInView={{ opacity: 1, y: 0 }}
                                      viewport={{ once: true }}
                                      transition={{ duration: 0.6, delay: index * 0.1 }}
                                      className="group h-96 relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer hover:scale-[1.02] hover:ring-2 hover:ring-brand-mint/30 w-full"
                                    >
                                      {/* Training Image */}
                                      <div className="relative w-full h-full">
                                        {training.banner_image ? (
                                          <img
                                            src={`${API_ENDPOINTS.UPLOADS}/${training.banner_image}`}
                                            alt={training.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <div className="w-full h-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center">
                                            <GraduationCap className="h-16 w-16 text-brand-primary/60" />
                                          </div>
                                        )}

                                        {/* Brand Color Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/40 via-brand-secondary/30 to-brand-mint/20 group-hover:from-brand-primary/30 group-hover:via-brand-secondary/20 group-hover:to-brand-mint/10 transition-all duration-300" />

                                        {/* Training Title with proper wrapping */}
                                        <div className="absolute bottom-6 left-6 right-20">
                                          <h3 className="text-white text-lg font-bold leading-tight line-clamp-2">
                                            {training.title}
                                          </h3>
                                        </div>

                                        {/* Arrow Icon */}
                                        <div className="absolute bottom-6 right-6">
                                          <div className="w-10 h-10 bg-transparent rounded-full flex items-center justify-center group-hover:bg-brand-mint/30 group-hover:border-2 group-hover:border-brand-mint/50 transition-all duration-300">
                                            <ArrowRight className="h-5 w-5 text-white group-hover:text-brand-mint transition-colors duration-300" />
                                          </div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  </Link>
                                </div>
                              ));
                            })()}
                          </motion.div>
                        </div>
                      </div>

                      {/* Navigation Arrows */}
                      {(() => {
                        const featuredList = featuredTrainings.filter(t => t.is_featured);
                        const totalSlides = Math.max(0, featuredList.length - cardsPerView);
                        return featuredList.length > cardsPerView && (
                          <>
                            <button
                              onClick={() => {
                                if (currentSlide > 0) {
                                  setCurrentSlide((prev) => prev - 1);
                                }
                              }}
                              disabled={currentSlide === 0}
                              className={`absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-10 ${currentSlide === 0
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-white hover:scale-110'
                                }`}
                              aria-label="Previous slide"
                            >
                              <ChevronLeft className="h-6 w-6 text-gray-800" />
                            </button>

                            <button
                              onClick={() => {
                                const featuredList = featuredTrainings.filter(t => t.is_featured);
                                const totalSlides = Math.max(0, featuredList.length - cardsPerView);
                                if (currentSlide < totalSlides) {
                                  setCurrentSlide((prev) => prev + 1);
                                }
                              }}
                              disabled={currentSlide >= Math.max(0, featuredTrainings.filter(t => t.is_featured).length - cardsPerView)}
                              className={`absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-10 ${currentSlide >= Math.max(0, featuredTrainings.filter(t => t.is_featured).length - cardsPerView)
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-white hover:scale-110'
                                }`}
                              aria-label="Next slide"
                            >
                              <ChevronRight className="h-6 w-6 text-gray-800" />
                            </button>
                          </>
                        );
                      })()}
                    </div>

                    {/* Pagination Dots */}
                    {(() => {
                      const featuredList = featuredTrainings.filter(t => t.is_featured);
                      const totalSlides = Math.max(0, featuredList.length - cardsPerView) + 1;
                      return featuredList.length > cardsPerView && (
                        <div className="flex justify-center gap-2 mt-6">
                          {Array.from({ length: totalSlides }).map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentSlide(index)}
                              className={`h-2 rounded-full transition-all duration-300 ${currentSlide === index
                                ? 'w-8 bg-brand-secondary'
                                : 'w-2 bg-gray-300 hover:bg-gray-400'
                                }`}
                              aria-label={`Go to slide ${index + 1}`}
                            />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <GraduationCap className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.featured.noFeatured')}</h3>
                    <p className="text-gray-600 mb-4">{t('home.featured.noFeaturedDesc')}</p>
                    <Link
                      to="/trainings"
                      className="inline-flex items-center text-brand-primary hover:text-purple-700 font-semibold"
                    >
                      {t('home.featured.viewAllTrainings')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                )}
              </motion.div>
            </div>

            {/* View All Trainings Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center mt-12"
            >
              <Link
                to="/trainings"
                className="inline-flex items-center text-brand-primary hover:text-purple-700 font-semibold text-lg group"
              >
                {t('home.featured.viewAllTrainings')}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </ScrollReveal>

      {/* Mission Statement Section - COMMENTED OUT FOR DEVELOPMENT */}



      {/* Global Calendar Section */}
      <ScrollReveal delay={0.2}>
        <section id="calendar" className="py-24 bg-gradient-to-br from-brand-surface via-white to-brand-primary/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
            >
              {/* Calendar Header */}
              <div className="bg-brand-primary p-4 sm:p-8">
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-lg sm:text-2xl font-bold text-white">{t('home.calendar.title')}</h3>
                      <p className="text-white/80 text-sm sm:text-base">{t('home.calendar.subtitle')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FullCalendar */}
              <div className="p-4 sm:p-8">
                {calendarLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
                  </div>
                ) : (
                  <div className={`fullcalendar-container ${isMobile ? 'mobile-calendar' : ''}`}>
                    <FullCalendar
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView={isMobile ? 'dayGridMonth' : viewMode}
                      headerToolbar={isMobile ? {
                        left: 'prev,next',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek'
                      } : {
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                      }}
                      events={calendarEvents}
                      eventClick={handleEventClick}
                      eventMouseEnter={isMobile ? undefined : handleEventMouseEnter}
                      eventMouseLeave={isMobile ? undefined : handleEventMouseLeave}
                      select={handleDateSelect}
                      viewDidMount={handleViewChange}
                      locale="fr"
                      firstDay={1} // Start week on Monday
                      height={isMobile ? 350 : "auto"}
                      eventDisplay="block"
                      dayMaxEvents={isMobile ? 2 : 3}
                      moreLinkClick="popover"
                      eventTextColor="#FFFFFF"
                      eventBorderColor="transparent"
                      eventBackgroundColor="#FF2E7F"
                      eventClassNames="training-event"
                      slotMinTime="08:00:00"
                      slotMaxTime="20:00:00"
                      allDaySlot={false}
                      nowIndicator={true}
                      selectable={!isMobile}
                      selectMirror={!isMobile}
                      weekends={true}
                      businessHours={{
                        daysOfWeek: [1, 2, 3, 4, 5, 6, 7], // Monday - Friday
                        startTime: '08:00',
                        endTime: '18:00',
                      }}
                      eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }}
                      slotLabelFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }}
                      buttonText={{
                        today: t('home.calendar.buttonText.today'),
                        month: t('home.calendar.buttonText.month'),
                        week: t('home.calendar.buttonText.week'),
                        day: t('home.calendar.buttonText.day'),
                        list: t('home.calendar.buttonText.list')
                      }}
                      titleFormat={{
                        year: 'numeric',
                        month: 'long'
                      }}
                      dayHeaderFormat={{
                        weekday: isMobile ? 'narrow' : 'short'
                      }}
                      eventContent={(eventInfo: any) => (
                        <div className="training-event-content">
                          <div className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'} truncate`}>
                            {eventInfo.event.title}
                          </div>
                          {!isMobile && (
                            <div className="text-xs opacity-90 truncate">
                              {eventInfo.event.extendedProps.center}
                            </div>
                          )}
                        </div>
                      )}
                      dayCellContent={(arg) => {
                        if (isMobile) {
                          return <div className="text-xs font-medium">{arg.dayNumberText}</div>;
                        }
                        return arg.dayNumberText;
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Hover Card */}
              {hoveredEvent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="fixed z-50"
                  style={{
                    left: `${hoverPosition.x}px`,
                    top: `${hoverPosition.y}px`,
                    transform: 'translateX(-50%) translateY(-100%)'
                  }}
                  onMouseEnter={handleCardMouseEnter}
                  onMouseLeave={handleCardMouseLeave}
                >
                  <div
                    className="backdrop-blur-sm rounded-2xl shadow-2xl p-6 max-w-sm"
                    style={{
                      backgroundColor: hoveredEventColor,
                      borderColor: `${hoveredEventColor}20`,
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    {/* Card Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-white text-lg mb-1">{hoveredEvent.title}</h4>
                        <p className="text-white/90 text-sm">{hoveredEvent.center}</p>
                      </div>
                    </div>

                    {/* Training Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-3 text-white/90">
                        <MapPin className="h-4 w-4 text-white/70" />
                        <span className="text-sm">{hoveredEvent.location}</span>
                      </div>

                      <div className="flex items-center gap-3 text-white/90">
                        <Clock className="h-4 w-4 text-white/70" />
                        <span className="text-sm">
                          {hoveredEvent.sessionDate && new Date(hoveredEvent.sessionDate).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-white/90">
                        <span className="text-sm font-semibold">💰 {hoveredEvent.price} DA</span>
                      </div>

                      <div className="flex items-center gap-3 text-white/90">
                        <Users className="h-4 w-4 text-white/70" />
                        <span className="text-sm">
                          {hoveredEvent.currentParticipants || 0}/{hoveredEvent.maxParticipants || 'N/A'} {t('home.calendar.hoverCard.participants')}
                        </span>
                      </div>

                      {hoveredEvent.specialties && hoveredEvent.specialties.length > 0 && (
                        <div className="flex items-start gap-3 text-white/90">
                          <Award className="h-4 w-4 text-white/70 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium mb-1">{t('home.calendar.hoverCard.specialties')}</div>
                            <div className="flex flex-wrap gap-1">
                              {hoveredEvent.specialties.slice(0, 2).map((specialty: string, index: number) => (
                                <span key={index} className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                                  {specialty}
                                </span>
                              ))}
                              {hoveredEvent.specialties.length > 2 && (
                                <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                                  +{hoveredEvent.specialties.length - 2}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <div className="pt-2 border-t border-white/20">
                      <Link
                        to={`/training/${hoveredEvent.trainingId}`}
                        className="w-full bg-white hover:bg-white/90 font-semibold py-2 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2"
                        style={{ color: hoveredEventColor }}
                        onClick={() => setHoveredEvent(null)}
                      >
                        {t('home.calendar.hoverCard.viewDetails')}
                      </Link>
                    </div>

                    {/* Arrow pointing down */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-brand-mint"></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>
      </ScrollReveal>

      {/* Contact Form Section */}
      <ScrollReveal delay={0.3}>
        <section className="py-24 bg-gradient-to-br from-brand-mint/10 via-white to-brand-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                {/* Contact Label */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="text-sm font-semibold text-brand-primary uppercase tracking-wider"
                >
                  {t('home.contact.label')}
                </motion.div>

                {/* Main Headline */}
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight"
                >
                  {t('home.contact.title')}
                </motion.h2>

                {/* Team Image */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative"
                >
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={contactImage}
                      alt="Notre équipe d'experts"
                      className="w-full h-80 object-cover"
                      onError={(e) => {
                        // Fallback to a gradient background if image doesn't exist
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    {/* Fallback gradient background */}
                    <div
                      className="w-full h-80 bg-gradient-to-br from-brand-primary/20 via-brand-secondary/20 to-brand-mint/20 flex items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <div className="text-center">
                        <Users className="h-16 w-16 text-brand-primary/60 mx-auto mb-4" />
                        <p className="text-gray-600 font-medium">Notre équipe d'experts</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Right Side - Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-gray-900 rounded-3xl p-8 shadow-2xl border border-gray-800">
                  {/* Form Header */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {t('home.contact.subtitle')}
                    </h3>
                  </div>

                  {/* Contact Form */}
                  <form className="space-y-6" onSubmit={handleContactSubmit}>
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('home.contact.fullName')}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          value={contactForm.name}
                          onChange={handleContactFormChange}
                          className="w-full px-4 py-4 bg-transparent border-b border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-mint transition-colors duration-300"
                          placeholder="Votre nom et prénom "
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('home.contact.email')}
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={contactForm.email}
                          onChange={handleContactFormChange}
                          className="w-full px-4 py-4 bg-transparent border-b border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-mint transition-colors duration-300"
                          placeholder="contact@gmail.com"
                          required
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('home.contact.message')}
                      </label>
                      <div className="relative">
                        <textarea
                          name="message"
                          value={contactForm.message}
                          onChange={handleContactFormChange}
                          rows={4}
                          className="w-full px-4 py-4 bg-transparent border-b border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-mint transition-colors duration-300 resize-none"
                          placeholder={t('home.contact.message')}
                          required
                        />
                      </div>
                    </div>

                    {/* Status Message */}
                    {submitMessage && (
                      <div className={`p-4 rounded-lg ${submitStatus === 'success'
                        ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                        : 'bg-red-500/20 border border-red-500/30 text-red-300'
                        }`}>
                        {submitMessage}
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="pt-6">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex items-center justify-center px-8 py-4 bg-brand-mint hover:bg-brand-mint/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold text-lg rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {t('home.contact.submitting')}
                          </>
                        ) : (
                          <>
                            {t('home.contact.submitButton')}
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </ScrollReveal>

    </div>
  );
};

export default HomePage;
